

import React, { useState, useEffect, useRef } from 'react';
import { Save, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const UpdateServiceDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams(); 
  const dropdownRef = useRef(null); 
  const [activeLanguage, setActiveLanguage] = useState('en');
  const [formData, setFormData] = useState({
    hero: { title: '', subtitle: '', paragraphs: [''], fileUrl: '' },
    projects: [],
    statistics: {
      ledStrips: { number: '', description: '' },
      retailSpace: { number: '', description: '' },
    },
  });

  const [spanishData, setSpanishData] = useState({
    hero: { title: '', subtitle: '', paragraphs: [''], fileUrl: '' },
    statistics: {
      ledStrips: { number: '', description: '' },
      retailSpace: { number: '', description: '' },
    },
  });

  const [heroFile, setHeroFile] = useState(null);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [existingHeroFileUrl, setExistingHeroFileUrl] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const serviceResponse = await axios.get(
          `${config.BASE_URL}/api/admin/services/${id}`,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
          }
        );
        const serviceData = serviceResponse.data.data || {};
        setFormData({
          hero: {
            title: serviceData.hero?.title || '',
            subtitle: serviceData.hero?.subtitle || '',
            paragraphs: serviceData.hero?.paragraphs?.length
              ? serviceData.hero.paragraphs
              : [''],
            fileUrl: serviceData.hero?.fileUrl || '',
          },
          projects: Array.isArray(serviceData.projects) ? serviceData.projects : [], // Sanitize projects
          statistics: {
            ledStrips: {
              number: serviceData.statistics?.ledStrips?.number || '',
              description: serviceData.statistics?.ledStrips?.description || '',
            },
            retailSpace: {
              number: serviceData.statistics?.retailSpace?.number || '',
              description: serviceData.statistics?.retailSpace?.description || '',
            },
          },
        });
        setSpanishData({
          hero: {
            title: serviceData.spanishData?.hero?.title || '',
            subtitle: serviceData.spanishData?.hero?.subtitle || '',
            paragraphs: serviceData.spanishData?.hero?.paragraphs?.length
              ? serviceData.spanishData.hero.paragraphs
              : [''],
            fileUrl: serviceData.spanishData?.hero?.fileUrl || '',
          },
          statistics: {
            ledStrips: {
              number: serviceData.spanishData?.statistics?.ledStrips?.number || '',
              description: serviceData.spanishData?.statistics?.ledStrips?.description || '',
            },
            retailSpace: {
              number: serviceData.spanishData?.statistics?.retailSpace?.number || '',
              description: serviceData.spanishData?.statistics?.retailSpace?.description || '',
            },
          },
        });
        setExistingHeroFileUrl(serviceData.hero?.fileUrl || '');
        const projectsResponse = await axios.get(
          `${config.BASE_URL}/api/admin/get-projects`,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
          }
        );
        setAvailableProjects(projectsResponse.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.response?.status === 401) {
          setErrors({ submit: 'Authentication failed. Please login again.' });
          setTimeout(() => navigate('/admin/login'), 2000);
        } else if (err.response?.status === 404) {
          setErrors({ submit: 'Service not found.' });
        } else {
          setErrors({ submit: 'Failed to fetch service data or projects.' });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);
  // Form Handlers - English
  const handleHeroChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      hero: { ...prev.hero, [field]: value },
    }));
  };

  const handleHeroParagraphChange = (index, value) => {
    const newParagraphs = [...formData.hero.paragraphs];
    newParagraphs[index] = value;
    setFormData((prev) => ({
      ...prev,
      hero: { ...prev.hero, paragraphs: newParagraphs },
    }));
  };

  const addHeroParagraph = () => {
    setFormData((prev) => ({
      ...prev,
      hero: { ...prev.hero, paragraphs: [...prev.hero.paragraphs, ''] },
    }));
  };

  const removeHeroParagraph = (index) => {
    if (formData.hero.paragraphs.length > 1) {
      const newParagraphs = formData.hero.paragraphs.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        hero: { ...prev.hero, paragraphs: newParagraphs },
      }));
    }
  };

  // Custom Multi-Select Handler
  // const handleProjectToggle = (projectId) => {
  //   const isSelected = formData.projects.some((p) => p.project === projectId);
  //   let newProjects;
  //   if (isSelected) {
  //     newProjects = formData.projects.filter((p) => p.project !== projectId);
  //   } else {
  //     newProjects = [
  //       ...formData.projects,
  //       { project: projectId, prioritNumber: (formData.projects.length + 1).toString() },
  //     ];
  //   }
  //   newProjects = newProjects.map((p, index) => ({
  //     ...p,
  //     prioritNumber: (index + 1).toString(),
  //   }));
  //   setFormData((prev) => ({ ...prev, projects: newProjects }));
  //   setIsDropdownOpen(false); // Close dropdown after selection
  // };
  const handleProjectToggle = (projectId) => {
    if (!projectId) return; // Prevent invalid projectId
    const isSelected = formData.projects.some((p) => p.project === projectId);
    let newProjects;
    if (isSelected) {
      newProjects = formData.projects.filter((p) => p.project !== projectId);
    } else {
      newProjects = [
        ...formData.projects,
        { project: projectId, prioritNumber: (formData.projects.length + 1).toString() },
      ];
    }
    newProjects = newProjects.map((p, index) => ({
      ...p,
      prioritNumber: (index + 1).toString(),
    }));
    setFormData((prev) => ({ ...prev, projects: newProjects }));
    setIsDropdownOpen(false);
  };
  const removeProject = (projectId) => {
    const newProjects = formData.projects
      .filter((p) => p.project !== projectId)
      .map((p, index) => ({ ...p, prioritNumber: (index + 1).toString() }));
    setFormData((prev) => ({ ...prev, projects: newProjects }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = formData.projects.findIndex((p) => p.project === active.id);
      const newIndex = formData.projects.findIndex((p) => p.project === over.id);
      const newProjects = arrayMove(formData.projects, oldIndex, newIndex).map(
        (project, index) => ({
          ...project,
          prioritNumber: (index + 1).toString(),
        })
      );
      setFormData((prev) => ({ ...prev, projects: newProjects }));
    }
  };

  const handleStatisticsChange = (category, field, value) => {
    setFormData((prev) => ({
      ...prev,
      statistics: {
        ...prev.statistics,
        [category]: { ...prev.statistics[category], [field]: value },
      },
    }));
  };

  // Sortable Project Item Component
  const SortableProjectItem = ({ project, title }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: project.project,
    });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      padding: '10px',
      backgroundColor: '#f8f9fa',
      border: '1px solid #ddd',
      borderRadius: '4px',
      marginBottom: '5px',
      cursor: 'grab',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      color: 'black'
    };
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <span>{title} </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            removeProject(project.project);
          }}
          style={{
            padding: '5px 10px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
          disabled={isSubmitting}
        >
          Remove
        </button>
      </div>
    );
  };

  // Form Handlers - Spanish
  const handleSpanishHeroChange = (field, value) => {
    setSpanishData((prev) => ({
      ...prev,
      hero: { ...prev.hero, [field]: value },
    }));
  };

  const handleSpanishHeroParagraphChange = (index, value) => {
    const newParagraphs = [...spanishData.hero.paragraphs];
    newParagraphs[index] = value;
    setSpanishData((prev) => ({
      ...prev,
      hero: { ...prev.hero, paragraphs: newParagraphs },
    }));
  };

  const addSpanishHeroParagraph = () => {
    setSpanishData((prev) => ({
      ...prev,
      hero: { ...prev.hero, paragraphs: [...prev.hero.paragraphs, ''] },
    }));
  };

  const removeSpanishHeroParagraph = (index) => {
    if (spanishData.hero.paragraphs.length > 1) {
      const newParagraphs = spanishData.hero.paragraphs.filter((_, i) => i !== index);
      setSpanishData((prev) => ({
        ...prev,
        hero: { ...prev.hero, paragraphs: newParagraphs },
      }));
    }
  };

  const handleSpanishStatisticsChange = (category, field, value) => {
    setSpanishData((prev) => ({
      ...prev,
      statistics: {
        ...prev.statistics,
        [category]: { ...prev.statistics[category], [field]: value },
      },
    }));
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.hero.title.trim()) {
      newErrors['hero.title'] = 'Hero title is required';
    }
    if (!formData.hero.subtitle.trim()) {
      newErrors['hero.subtitle'] = 'Hero subtitle is required';
    }
    if (activeLanguage === 'es') {
      if (!spanishData.hero.title.trim()) {
        newErrors['spanish.hero.title'] = 'Spanish hero title is required';
      }
      if (!spanishData.hero.subtitle.trim()) {
        newErrors['spanish.hero.subtitle'] = 'Spanish hero subtitle is required';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    try {
      const fd = new FormData();
      fd.append(
        'hero',
        JSON.stringify({
          title: formData.hero.title.trim(),
          subtitle: formData.hero.subtitle.trim(),
          paragraphs: formData.hero.paragraphs.filter((p) => p.trim()).map((p) => p.trim()),
          fileUrl: formData.hero.fileUrl || '',
        })
      );
      fd.append('projects', JSON.stringify(formData.projects));
      fd.append(
        'statistics',
        JSON.stringify({
          ledStrips: {
            number: formData.statistics.ledStrips.number.trim(),
            description: formData.statistics.ledStrips.description.trim(),
          },
          retailSpace: {
            number: formData.statistics.retailSpace.number.trim(),
            description: formData.statistics.retailSpace.description.trim(),
          },
        })
      );
      fd.append(
        'spanishData',
        JSON.stringify({
          hero: {
            title: spanishData.hero.title.trim(),
            subtitle: spanishData.hero.subtitle.trim(),
            paragraphs: spanishData.hero.paragraphs
              .filter((p) => p.trim())
              .map((p) => p.trim()),
            fileUrl: spanishData.hero.fileUrl || '',
          },
          statistics: {
            ledStrips: {
              number: spanishData.statistics.ledStrips.number.trim(),
              description: spanishData.statistics.ledStrips.description.trim(),
            },
            retailSpace: {
              number: spanishData.statistics.retailSpace.number.trim(),
              description: spanishData.statistics.retailSpace.description.trim(),
            },
          },
        })
      );
      if (heroFile) {
        fd.append('heroFile', heroFile);
      }
      const response = await axios.put(
        `${config.BASE_URL}/api/admin/services/${id}`,
        fd,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      if (response.status === 200) {
        setSuccessMessage('Service content updated successfully!');
        const updated = response?.data?.data;

        setTimeout(() => navigate('/admin/services'), 2000);
      }
    } catch (err) {
      console.error('Error updating service data:', err);
      let errorMessage = 'Failed to update service data';
      if (err.response?.data?.message) {
        errorMessage = err?.response?.data?.message;
      } else if (err?.response?.status === 400) {
        errorMessage = 'Invalid data provided';
      } else if (err?.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
        setTimeout(() => navigate('/admin/login'), 2000);
      } else if (err?.response?.status === 404) {
        errorMessage = 'Service not found.';
      }
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render
  return (
    <div className="dashboard">
      <div className="container">
        <h1 className="dash_title">Update Service Page</h1>
        <div className="main-section">
          <div className="back_btn_wrap">
            <button className="back-btn" onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="text-white">Loading...</p>
            </div>
          ) : (
            <div className="main-inner add_project_main">
              <div className="language-tabs" style={{ marginBottom: '20px' }}>
                <button
                  type="button"
                  onClick={() => setActiveLanguage('en')}
                  style={{
                    padding: '10px 20px',
                    marginRight: '10px',
                    backgroundColor: activeLanguage === 'en' ? '#007bff' : '#f8f9fa',
                    color: activeLanguage === 'en' ? 'white' : 'black',
                    border: '1px solid #ddd',
                    cursor: 'pointer',
                    borderRadius: '4px',
                  }}
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLanguage('es')}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: activeLanguage === 'es' ? '#007bff' : '#f8f9fa',
                    color: activeLanguage === 'es' ? 'white' : 'black',
                    border: '1px solid #ddd',
                    cursor: 'pointer',
                    borderRadius: '4px',
                  }}
                >
                  Spanish
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                {successMessage && (
                  <div
                    style={{
                      padding: '12px',
                      marginBottom: '20px',
                      backgroundColor: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderRadius: '4px',
                      color: '#155724',
                    }}
                  >
                    {successMessage}
                  </div>
                )}
                {activeLanguage === 'en' && (
                  <div>
                    <h3 style={{ color: '#fff', marginBottom: '20px' }}>English Content</h3>
                    <div
                      className="section-group"
                      style={{
                        marginBottom: '30px',
                        padding: '20px',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: '8px',
                      }}
                    >
                      <h4 style={{ color: '#ffd700', marginBottom: '15px', fontSize: '18px' }}>
                        Hero Banner
                      </h4>
                      <div className="add_field titleField">
                        <h2>Title *</h2>
                        <input
                          type="text"
                          value={formData.hero.title}
                          onChange={(e) => handleHeroChange('title', e.target.value)}
                          placeholder="Enter hero title"
                          required
                          disabled={isSubmitting}
                        />
                        {errors['hero.title'] && <p className="error-text">{errors['hero.title']}</p>}
                      </div>
                      <div className="add_field titleField">
                        <h2>Subtitle *</h2>
                        <input
                          type="text"
                          value={formData.hero.subtitle}
                          onChange={(e) => handleHeroChange('subtitle', e.target.value)}
                          placeholder="Enter hero subtitle"
                          required
                          disabled={isSubmitting}
                        />
                        {errors['hero.subtitle'] && (
                          <p className="error-text">{errors['hero.subtitle']}</p>
                        )}
                      </div>
                      <div>
                        <h2>Description</h2>
                        {formData.hero.paragraphs.map((para, index) => (
                          <div key={index} style={{ marginBottom: '10px' }} className="add_field titleField">
                            <textarea
                              value={para}
                              onChange={(e) => handleHeroParagraphChange(index, e.target.value)}
                              rows="3"
                              placeholder={`Description `}
                              disabled={isSubmitting}
                            />
                            {formData.hero.paragraphs.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeHeroParagraph(index)}
                                style={{
                                  marginTop: '5px',
                                  padding: '5px 15px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  cursor: 'pointer',
                                  borderRadius: '4px',
                                }}
                                disabled={isSubmitting}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        {/* <button
                          type="button"
                          onClick={addHeroParagraph}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            borderRadius: '4px',
                          }}
                          disabled={isSubmitting}
                        >
                          + Add Paragraph
                        </button> */}
                      </div>
                      <div className="add_field titleField">
                        <h2>Image File</h2>
                        <input
                          type="file"
                          accept="image/*,*"
                          onChange={(e) =>
                            setHeroFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)
                          }
                          disabled={isSubmitting}
                        />
                        {(heroFile || existingHeroFileUrl) && (
                          <div style={{ marginTop: '10px' }} className="wid-150">
                            {(heroFile?.type?.startsWith('video/') ||
                              (existingHeroFileUrl && existingHeroFileUrl.match(/\.(mp4|webm|ogg)$/i))) ? (
                              <video
                                controls
                                src={
                                  heroFile
                                    ? URL.createObjectURL(heroFile)
                                    : `${config.BASE_URL}/${existingHeroFileUrl}`
                                }
                                style={{ maxWidth: '100%', height: 'auto' }}
                              />
                            ) : (
                              <img
                                src={
                                  heroFile
                                    ? URL.createObjectURL(heroFile)
                                    : `${config.BASE_URL}/${existingHeroFileUrl}`
                                }
                                alt="Hero"
                                style={{ maxWidth: '100%', height: 'auto' }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      className="section-group"
                      style={{
                        marginBottom: '30px',
                        padding: '20px',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: '8px',
                      }}
                    >
                      <h4 style={{ color: '#ffd700', marginBottom: '15px', fontSize: '18px' }}>
                        Projects
                      </h4>
                      <div className="add_field titleField">
                        <h2>Select Projects</h2>
                        <div ref={dropdownRef} style={{ position: 'relative' }}>
                          <div
                            onClick={() => !isSubmitting && setIsDropdownOpen(!isDropdownOpen)}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              cursor: isSubmitting ? 'not-allowed' : 'pointer',
                              minHeight: '40px',
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '5px',
                              alignItems: 'center',
                              color: 'black'
                            }}
                          >
                            {formData?.projects?.length === 0 ? (
                              <span style={{ color: '#999' }}>Select projects...</span>
                            ) : (
                              // formData?.projects?.map((p) => {
                              //   const project = availableProjects?.find((ap) => ap?._id === p?.project);
                              //   return (
                              //     <span
                              //       key={p?.project}
                              //       style={{
                              //         backgroundColor: '#007bff',
                              //         color: 'white',
                              //         padding: '3px 8px',
                              //         borderRadius: '3px',
                              //         fontSize: '14px',
                              //         display: 'flex',
                              //         alignItems: 'center',
                              //         gap: '5px',
                              //       }}
                              //     >
                              //       {project?.title || p?.project}
                              //       <X
                              //         size={14}
                              //         onClick={(e) => {
                              //           e.stopPropagation();
                              //           removeProject(p.project);
                              //         }}
                              //         style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                              //       />
                              //     </span>
                              //   );
                              // })
                              // {
                              formData.projects
                                .filter((p) => p && p.project) // Ensure no null/undefined projects
                                .map((p) => {
                                  const project = availableProjects.find((ap) => ap._id === p.project);
                                  return (
                                    <span
                                      key={`dropdown-${p.project}`} // Ensure unique key with prefix
                                      style={{
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        padding: '3px 8px',
                                        borderRadius: '3px',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                      }}
                                    >
                                      {project?.title || p.project}
                                      <X
                                        size={14}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeProject(p.project);
                                        }}
                                        style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                                      />
                                    </span>
                                  );
                                })
                              // }
                            )}
                          </div>
                          {isDropdownOpen && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                marginTop: '5px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                zIndex: 1000,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              }}
                            >
                              {availableProjects.length === 0 ? (
                                <div style={{ padding: '10px', color: '#999' }}>
                                  No projects available
                                </div>
                              ) : (
                                availableProjects.map((project) => {
                                  const isSelected = formData.projects.some(
                                    (p) => p.project === project._id
                                  );
                                  return (
                                    <div
                                      key={project._id}
                                      onClick={() => !isSubmitting && handleProjectToggle(project._id)}
                                      style={{
                                        padding: '10px',
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                        backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                        borderBottom: '1px solid #f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        color: 'black'
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSelected && !isSubmitting)
                                          e.target.style.backgroundColor = '#f5f5f5';
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSelected && !isSubmitting)
                                          e.target.style.backgroundColor = 'white';
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => { }}
                                        disabled={isSubmitting}
                                        style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                                      />
                                      <span>{project.title || project._id}</span>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* {formData.projects.length > 0 && (
                        <div className="add_field titleField" style={{ marginTop: '15px' }}>
                          <h2>Selected Projects (Drag to Reorder)</h2>
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                          >
                            <SortableContext
                              items={formData.projects.map((p) => p.project)}
                              strategy={verticalListSortingStrategy}
                            >
                              {formData.projects.map((project) => (
                                <SortableProjectItem
                                  key={project.project}
                                  project={project}
                                  title={
                                    availableProjects.find((p) => p._id === project.project)?.title ||
                                    project.project
                                  }
                                />
                              ))}
                            </SortableContext>
                          </DndContext>
                        </div>
                      )} */}

                      {formData.projects.length > 0 && (
                        <div className=" titleField" style={{ marginTop: '15px' }}>
                          <h2>Selected Projects (Drag to Reorder)</h2>
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                          >
                            <SortableContext
                              items={formData?.projects
                                .filter((p) => p && p?.project) // Ensure no null/undefined projects
                                .map((p) => p?.project)}
                              strategy={verticalListSortingStrategy}
                            >
                              {formData?.projects
                                .filter((p) => p && p?.project) // Filter again for rendering
                                .map((project, ind) => (
                                  <SortableProjectItem
                                    key={ind}
                                    project={project}
                                    title={
                                      availableProjects?.find((p) => p._id === project?.project)?.title ||
                                      project?.project
                                    }
                                  />
                                ))}
                              {/* {formData.projects
                                .filter((p) => p && p.project)
                                .map((project, index) => (
                                  <SortableProjectItem
                                    key={`sortable-${project.project}-${index}`} // Ensure unique key with prefix and index
                                    project={project}
                                    title={
                                      availableProjects.find((p) => p._id === project.project)?.title ||
                                      project.project
                                    }
                                  />
                                ))} */}

                            </SortableContext>
                          </DndContext>
                        </div>
                      )}
                    </div>
                    <div
                      className="section-group"
                      style={{
                        marginBottom: '30px',
                        padding: '20px',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: '8px',
                      }}
                    >
                      <h4 style={{ color: '#ffd700', marginBottom: '15px', fontSize: '18px' }}>
                        Statistics
                      </h4>
                      <div className="add_field titleField">
                        <h2>LED Strips Number</h2>
                        <input
                          type="text"
                          value={formData.statistics.ledStrips.number}
                          onChange={(e) =>
                            handleStatisticsChange('ledStrips', 'number', e.target.value)
                          }
                          placeholder="e.g., 10,000+"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>LED Strips Description</h2>
                        <input
                          type="text"
                          value={formData.statistics.ledStrips.description}
                          onChange={(e) =>
                            handleStatisticsChange('ledStrips', 'description', e.target.value)
                          }
                          placeholder="Enter LED strips description"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Retail Space Number</h2>
                        <input
                          type="text"
                          value={formData.statistics.retailSpace.number}
                          onChange={(e) =>
                            handleStatisticsChange('retailSpace', 'number', e.target.value)
                          }
                          placeholder="e.g., 50,000 sq ft"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Retail Space Description</h2>
                        <input
                          type="text"
                          value={formData.statistics.retailSpace.description}
                          onChange={(e) =>
                            handleStatisticsChange('retailSpace', 'description', e.target.value)
                          }
                          placeholder="Enter retail space description"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {activeLanguage === 'es' && (
                  <div>
                    <h3 style={{ color: '#fff', marginBottom: '20px' }}>
                      Spanish Content (Contenido en Español)
                    </h3>
                    <div
                      className="section-group"
                      style={{
                        marginBottom: '30px',
                        padding: '20px',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: '8px',
                      }}
                    >
                      <h4 style={{ color: '#ffd700', marginBottom: '15px', fontSize: '18px' }}>
                        Banner Principal
                      </h4>
                      <div className="add_field titleField">
                        <h2>Título *</h2>
                        <input
                          type="text"
                          value={spanishData.hero.title}
                          onChange={(e) => handleSpanishHeroChange('title', e.target.value)}
                          placeholder="Ingrese el título del hero"
                          required
                          disabled={isSubmitting}
                        />
                        {errors['spanish.hero.title'] && (
                          <p className="error-text">{errors['spanish.hero.title']}</p>
                        )}
                      </div>
                      <div className="add_field titleField">
                        <h2>Subtítulo *</h2>
                        <input
                          type="text"
                          value={spanishData.hero.subtitle}
                          onChange={(e) => handleSpanishHeroChange('subtitle', e.target.value)}
                          placeholder="Ingrese el subtítulo del hero"
                          required
                          disabled={isSubmitting}
                        />
                        {errors['spanish.hero.subtitle'] && (
                          <p className="error-text">{errors['spanish.hero.subtitle']}</p>
                        )}
                      </div>
                      <div>
                        <h2>Párrafos</h2>
                        {spanishData.hero.paragraphs.map((para, index) => (
                          <div key={index} style={{ marginBottom: '10px' }} className="add_field titleField">
                            <textarea
                              value={para}
                              onChange={(e) => handleSpanishHeroParagraphChange(index, e.target.value)}
                              rows="3"
                              placeholder={`Párrafo ${index + 1}`}
                              disabled={isSubmitting}
                            />
                            {spanishData.hero.paragraphs.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeSpanishHeroParagraph(index)}
                                style={{
                                  marginTop: '5px',
                                  padding: '5px 15px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  cursor: 'pointer',
                                  borderRadius: '4px',
                                }}
                                disabled={isSubmitting}
                              >
                                Eliminar
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addSpanishHeroParagraph}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            borderRadius: '4px',
                          }}
                          disabled={isSubmitting}
                        >
                          + Agregar Párrafo
                        </button>
                      </div>
                    </div>
                    <div
                      className="section-group"
                      style={{
                        marginBottom: '30px',
                        padding: '20px',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: '8px',
                      }}
                    >
                      <h4 style={{ color: '#ffd700', marginBottom: '15px', fontSize: '18px' }}>
                        Estadísticas
                      </h4>
                      <div className="add_field titleField">
                        <h2>Número de Tiras LED</h2>
                        <input
                          type="text"
                          value={spanishData.statistics.ledStrips.number}
                          onChange={(e) =>
                            handleSpanishStatisticsChange('ledStrips', 'number', e.target.value)
                          }
                          placeholder="ej., 10,000+"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Descripción de Tiras LED</h2>
                        <input
                          type="text"
                          value={spanishData.statistics.ledStrips.description}
                          onChange={(e) =>
                            handleSpanishStatisticsChange('ledStrips', 'description', e.target.value)
                          }
                          placeholder="Ingrese la descripción de las tiras LED"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Número de Espacio Comercial</h2>
                        <input
                          type="text"
                          value={spanishData.statistics.retailSpace.number}
                          onChange={(e) =>
                            handleSpanishStatisticsChange('retailSpace', 'number', e.target.value)
                          }
                          placeholder="ej., 50,000 pies cuadrados"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Descripción de Espacio Comercial</h2>
                        <input
                          type="text"
                          value={spanishData.statistics.retailSpace.description}
                          onChange={(e) =>
                            handleSpanishStatisticsChange('retailSpace', 'description', e.target.value)
                          }
                          placeholder="Ingrese la descripción del espacio comercial"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="add_project" style={{ marginTop: '30px' }}>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Save className="w-5 h-5" />
                    {isSubmitting ? 'Updating...' : 'Update'}
                  </button>
                </div>
                {errors.submit && (
                  <div
                    style={{
                      padding: '12px',
                      marginTop: '20px',
                      backgroundColor: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      borderRadius: '4px',
                      color: '#721c24',
                    }}
                  >
                    {errors.submit}
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateServiceDetail;





